import log from 'loglevel';
import HlsJS from 'hls.js';
import { Html5 } from '../html5/Html5';
import ProviderInterface from '../../ProviderInterface';
import {
  InitOptions,
  State,
  Source,
  QualityLevel,
  AudioTrack,
  TextTrack,
  TextTrackKind,
  Provider,
  PlaybackMode,
  QualityLevelKind,
  HlsProviderOptions
} from '../../../types/player';
import { QualityLevelEvents } from '../../PlayerEvents';
//import { LevelLoadedData, ManifestLoadedData, ErrorData }  from 'hls.js/src/types/events';
import { ErrorCode, Error, ErrorMessage } from '../../../types/error';
import { PlayerEvents } from '../../PlayerEvents';
import { Options } from '../../optionsManager/Options';

class Hls extends Html5 implements ProviderInterface {
  hlsJS: HlsJS;
  firstLoaded = false;
  loadRetryer: ReturnType<typeof setTimeout> = null as any;
  lastPlayPosition = -1;
  dvrWindow = 0;
  src: string;
  isHlsSubtitlesEnabled = false;
  hlsOptions: HlsProviderOptions = null as any;
  lastCurrentTime: number = 0;

  constructor(element: HTMLElement, options: Options, events: PlayerEvents) {
    super(element, options, events);
    log.debug('events', events);
    this.hlsJS = new HlsJS(options.getProviderNativeOptions(Provider.Hls));
    //@ts-ignore
    //window.hlsJS = this.hlsJS;
    log.debug('Hls init', this.hlsJS);
    this.hlsOptions = options.getProviderHlsOptions() as HlsProviderOptions;
    this.src = '';
    this._isDvr = options.getSource().playbackMode === PlaybackMode.DVR;    
  }

  onManifestLoaded(): void {
    console.log('Hls onManifestLoaded');

    this.qualityLevels = {
      audio: [],
      video: [],
    };
    this.qualityLevelCurrentIndex = {
      audio: -1,
      video: -1,
    };
    this.videoTracks = [];
    this.textTracks = [];
  
    this.currentVideoTrack = -1;
    this.currentTextTrack = -1;


    for (let i = 0; i < this.hlsJS.levels.length; i++) {
      const qualityLevel = this.hlsJS.levels[i];

      if (qualityLevel.details?.live && this.options.getSource().playbackMode != PlaybackMode.DVR) this._isLive = true;

      this.qualityLevels[QualityLevelKind.VIDEO].push({
        bitrate: qualityLevel.bitrate,
        height: qualityLevel.height,
        width: qualityLevel.width,
        index: i,
        //label: qualityLevel.width + "x" + qualityLevel.height + ", " + sizeHumanizer(qualityLevel.bitrate, true, "bps")
        //in react, it is better to use like this.
      });
    }

    log.debug('QualityLevelsSelector ', 'this.hlsJS.firstLevel', this.hlsJS.firstLevel)
    this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO] = this.hlsJS.firstLevel;

    if (!this.options.getAlternativeLanguageSources()) {
      for (let i = 0; i < this.hlsJS.audioTracks.length; i++) {
        const audioTrack = this.hlsJS.audioTracks[i];

        this.audioTracks.push({
          index: audioTrack.id,
          label: audioTrack.name,
          data: audioTrack as any,
        });

        if (audioTrack.default === true) {
          this.currentAudioTrack = audioTrack.id;
        }
      }
    }

    //loop on textTracks
    if(this.hlsJS.subtitleTracks.length > 0) {
      this.isHlsSubtitlesEnabled = true;
      if(this.textTracks.length > 0) {
        this.textTracks = [];
        console.warn('Becarefule, subtitles are already set, it will be reseted because of native hls subtitles');
      }      
    }
    for (let i = 0; i < this.hlsJS.subtitleTracks.length; i++) {
      const textTrackTrack = this.hlsJS.subtitleTracks[i];
      this.textTracks.push({
        index: textTrackTrack.id,
        label: textTrackTrack.name,
        src: '',
        srclang: '',
        kind: TextTrackKind.SUBTITLES,
        data: textTrackTrack as any,
      });

      if (textTrackTrack.default === true) {
        this.currentTextTrack = textTrackTrack.id;
      }
    }
  }

  onManifestParsed(): void {
    log.debug('Hls onManifestParsed');    
  }

  //LevelLoadedData
  onLevelLoaded(event: any, data: any): void {
    console.log('Hls onLevelLoaded', data);

    this.firstLoaded = true;

    this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO] = data.level;

    this._events.qualityLevelChanged(
      this.getCurrentQualityLevel(QualityLevelKind.VIDEO),
      this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO],
      this.hlsJS.autoLevelEnabled      
    );

    if (this.loadRetryer) {
      clearTimeout(this.loadRetryer);
      this.loadRetryer = null as any;
    }

    if ((data.details.live && this.options.getSource().playbackMode != PlaybackMode.DVR) || this.options.getSource().playbackMode == PlaybackMode.LIVE) {
      this._isLive = true;
    } else {
      if (this.lastPlayPosition && this.lastPlayPosition >= 0) {
        this.seek(this.lastPlayPosition);
      }
    }
  }

  onLevelSwitched(event: any, data: any): void {
    log.debug('Hls onLevelSwitched', data.level);

    this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO] = data.level;

    //this.setCurrentQualityLevel(data.level);
    this._events.qualityLevelChanged(
      this.getCurrentQualityLevel(QualityLevelKind.VIDEO),
      this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO],
      this.hlsJS.autoLevelEnabled      
    );
  }

  onLevelAudioTrackSwitched(event: any, data: any): void {
    log.debug('Hls onLevelAudioTrackSwitched');

    this.currentAudioTrack = this.hlsJS.audioTrack;
    //this.setCurrentAudioTrack(data.id);
    this._events.audioTrackChanged(
      this.getCurrentAudioTrack(),
      this.hlsJS.audioTrack,
    );
  }

  onLevelUpdated(event: any, data: any): void {
    //log.debug('Hls onLevelUpdated');

   
    if(this._isDvr && data.details.totalduration > 0) {
      const delayStillConsideringLive = this.options.getDvrSettings().delayStillConsideringLive as number; 
      if(this.getCurrentTime() < data.details.totalduration - delayStillConsideringLive) {
        this._isDvrIsLive = false;
      }
      else {
        this._isDvrIsLive = true;
      }
      log.debug('data.details.totalduration', data.details.totalduration - delayStillConsideringLive, this.getCurrentTime(), this._isDvrIsLive)
    }    


    if (data && data.details) {
      this.dvrWindow = data.details.totalduration; //check if it is correct
    }
  }

  onLevelSwitching(event: any, data: any): void {
    log.debug('Hls onLevelSwitching', data);

    //this._events.qualityLevelSwitching(data.level, this.getCurrentQualityLevel());
  }

  reload(timeout = 3000) {
    this.killReloader();
    this.loadRetryer = setTimeout(() => {
      console.log('try to reload ', this.currentState)
      if(this.currentState === State.PLAYING) 
        return;
      if (this.hlsJS) {
        /*this.stop();        
        this.hlsJS.stopLoad();*/
        log.debug('Hls onError retrying...');
        let currentTime = this.getCurrentTime();
        this.hlsJS.loadSource(this.src);        
        if(this.options.getSource().playbackMode != PlaybackMode.LIVE) {          
          this.hlsJS.once(HlsJS.Events.MANIFEST_LOADED, () => {            
            console.log('try to reload seeking to', currentTime);
            this.seek(currentTime);
            this.play();
          });        
        }
      }
      else {
        log.debug('Hls onError retrying...')
        this.init(this.src);
      }
    }, timeout);
  }

  killReloader() {
    if (this.loadRetryer) {
      clearTimeout(this.loadRetryer);
      this.loadRetryer = null as any;
    }
  }

  onHlsJsError(event: any, data: any): void | null {
    log.debug('Hls onError', data);

    
    if (data && data.networkDetails && data.networkDetails.status === 202) { 
      if(this.options.getLiveSettings().retryIfNoLive)
        this.reload(this.options.getLiveSettings().retryTimeout);
      return;
    }

    if (!data.fatal) {
      //we reload if "bufferStalledError" because it is not fatal but ...      
      log.debug('not fatal error', data);
      this.reload(this.options.getLiveSettings().retryTimeout);
      return null;
    }

    let errorCode: ErrorCode = ErrorCode.UNKNOWN_ERROR;

    switch (data.type) {
      case HlsJS.ErrorTypes.NETWORK_ERROR:
        if (data && data.networkDetails && data.networkDetails.status === 400) {
          errorCode = ErrorCode.PLAYER_BAD_REQUEST_ERROR;
        } else if (
          data &&
          data.networkDetails &&
          data.networkDetails.status === 403
        ) {
          errorCode = ErrorCode.PLAYER_AUTH_FAILED_ERROR;
        } else if (
          data &&
          data.networkDetails &&
          data.networkDetails.status === 406
        ) {
          errorCode = ErrorCode.PLAYER_NOT_ACCEPTABLE_ERROR;
        }
        break;
      case HlsJS.ErrorTypes.MEDIA_ERROR:
        console.error('Erreur média, tentatives de récupération...');
        errorCode = ErrorCode.MEDIA_ERROR;
        break;
      default:
        break;
    }

    //alert('ok: ' + JSON.stringify(data));

    console.log('FATAL ERROR')

    this.onFatalError();

    if(
      (this.options.getSource().playbackMode === PlaybackMode.DVR 
      || this.options.getSource().playbackMode === PlaybackMode.LIVE)
      && this.options.getLiveSettings().retryIfNoLive) {
      //log.debug('we are checking if it is a livestream, so we try to reload the source');      
      errorCode = 5;
      this._events.error({
        code: errorCode,
        message: ErrorMessage.codes[errorCode].message,
      });
      log.debug('Hls live not started', 'reloading')
      this.reload(this.options.getLiveSettings().retryTimeout);
      return;
    }    

    this._events.error({
      code: errorCode,
      message: ErrorMessage.codes[errorCode].message,
    });

    if(this.hlsOptions.reloadOnFatalError && this.hlsOptions.reloadOnFatalError >= 0) {
      log.debug('Hls error fatal', 'reloading')
      this.reload(this.hlsOptions.reloadOnFatalError);
    }
  }

  init(src: string) {
    this.src = src;
    if (HlsJS.isSupported()) {
      this.hlsJS.loadSource(src);
      this.hlsJS.attachMedia(this.videoElement);
    } else {
      this.videoElement.src = src;
      log.debug('Hls is not supported');
      return;
    }

    this.hlsJS.once(
      HlsJS.Events.MANIFEST_LOADED,
      this.onManifestLoaded.bind(this),
    );

    this.hlsJS.once(
      HlsJS.Events.MANIFEST_PARSED,
      this.onManifestParsed.bind(this),
    );

    this.hlsJS.once(HlsJS.Events.LEVEL_LOADED, this.onLevelLoaded.bind(this));

    this.hlsJS.on(HlsJS.Events.LEVEL_SWITCHED, this.onLevelSwitched.bind(this));

    this.hlsJS.on(
      HlsJS.Events.LEVEL_SWITCHING,
      this.onLevelSwitching.bind(this),
    );

    this.hlsJS.on(
      HlsJS.Events.AUDIO_TRACK_SWITCHED,
      this.onLevelAudioTrackSwitched.bind(this),
    );

    this.hlsJS.on(HlsJS.Events.LEVEL_UPDATED, this.onLevelUpdated.bind(this));

    this.hlsJS.on(HlsJS.Events.ERROR, this.onHlsJsError.bind(this));    

    /*this.on(PLAYER_STATE, function (data) {

            if (!firstLoaded && data.prevstate === STATE_LOADING && data.newstate === STATE_IDLE) {

                if (loadRetryer) {
                    clearTimeout(loadRetryer);
                    loadRetryer = null;
                }

                if (hlsJS) {

                    hlsJS.stopLoad();
                }
            }
        });*/
  }

  reloadSource(src: string) {
    this.hlsJS.off(
      HlsJS.Events.LEVEL_SWITCHED,
      this.onLevelSwitched.bind(this),
    );
    this.hlsJS.off(
      HlsJS.Events.LEVEL_SWITCHING,
      this.onLevelSwitching.bind(this),
    );
    this.hlsJS.off(
      HlsJS.Events.AUDIO_TRACK_SWITCHED,
      this.onLevelAudioTrackSwitched.bind(this),
    );
    this.hlsJS.off(HlsJS.Events.LEVEL_UPDATED, this.onLevelUpdated.bind(this));
    this.hlsJS.off(HlsJS.Events.ERROR, this.onHlsJsError.bind(this));
    this.init(src);
  }

  onTimeUpdate() {
    if(this._isDestroyed) return;
    super.onTimeUpdate();
    if(this.hlsOptions.preventLoopBack) {     
      if(this._isLive && !this._isDvr && this.getCurrentTime() < this.lastCurrentTime) {        
        //we are in live mode and the current time need to be superior than the last current time, we prevent hlsjs loopback issue
        this.hlsJS.loadSource(this.src);
        this.hlsJS.once(HlsJS.Events.MANIFEST_LOADED, () => {
          if(this.currentTextTrack !== -1) {
            this.setCurrentTextTrack(this.currentTextTrack);
          }
        });
      }      
      this.lastCurrentTime = this.getCurrentTime();
    }
  }

  isAutoQualityLevelAvailable(kind: QualityLevelKind = QualityLevelKind.VIDEO): boolean {
    if(kind === QualityLevelKind.VIDEO)
      return true;
    return false;
  }

  setCurrentQualityLevel(qualityLevelIndex: number, kind: QualityLevelKind = QualityLevelKind.VIDEO): QualityLevel {

    this.hlsJS.currentLevel = qualityLevelIndex;
    log.debug('Hls setCurrentQualityLevel', qualityLevelIndex);

    if (qualityLevelIndex !== -1)
      this.qualityLevelCurrentIndex[kind] = this.hlsJS.currentLevel;

    return this.getCurrentQualityLevel(kind);
  }

  isAutoQualityLevel(kind: QualityLevelKind = QualityLevelKind.VIDEO): boolean {
    if(kind === QualityLevelKind.VIDEO)
      return this.hlsJS.autoLevelEnabled;
    return false;
  }

  /*setAutoQualityLevel(isAuto: boolean) {
        if (isAuto) {
            this.hlsJS.currentLevel = -1;
        } else {
            this.hlsJS.currentLevel = this.hlsJS.currentLevel;
        }
    };*/

  setCurrentAudioTrack = (audioTrackIndex: number): AudioTrack => {
    this.hlsJS.audioTrack = audioTrackIndex;
    return this.getCurrentAudioTrack();
  };

  setCurrentTextTrack(index: number): TextTrack {
    if(this.isHlsSubtitlesEnabled) {
      this.hlsJS.subtitleTrack = index;
      this.currentTextTrack = index;

      this._events.textTrackChanged(this.getCurrentTextTrack(), index);

      return this.getCurrentTextTrack();
    }
    else {
      return super.setCurrentTextTrack(index);
    }
  }

  stop() {
    this.killReloader()

    if (this.hlsJS) {
      this.hlsJS.stopLoad();
    }
  }

  destroy() {
    super.destroy();
    
    this.killReloader()

    if (this.hlsJS) {
      this.hlsJS.destroy();
      //this.trigger(HLS_DESTROYED);
    }

    this.hlsJS = null as any;
  }

  getLibrary(): HlsJS | HTMLVideoElement {
    if (HlsJS.isSupported())
      return this.hlsJS;  
    else
      return this.videoElement;
  }
  
}

export { Hls };
